import { createContext, useContext, useEffect, useRef, useState, type CSSProperties, type JSX } from "react";
import type { RemoteTile as RemoteTileData } from "../types";
import { HandChip } from "./HandChip";
import { VideoView } from "./VideoView";
import { keepPlaying } from "../lib/keepPlaying";
import { CameraOffIcon, HangUpIcon, LinkIcon, MicOffIcon, PhoneIcon } from "./icons";
import { CopyButton, canShare, shareLink } from "./CopyButton";

interface RemoteTileProps {
  readonly tile: RemoteTileData;
  /** A small tile in the screen-share strip: the name sits bottom-left.
   * Otherwise it sits top-left, clear of the controls along the bottom. */
  readonly compact?: boolean;
  /** A phone line in the room (a direct call), for the host: hang it up. */
  readonly onHangUpPhone?: (callId: string) => void;
  /** For a phone line: this room's link, which the host sends so the person
   * can join with video. Their phone call stays up when they do (QUEUE row
   * 120: FaceTime from a phone call is not the way into the room). */
  readonly videoLink?: string;
  /** The host: lower this person's raised hand. */
  readonly onLowerHand?: (participantId: string) => void;
  /** Their picture's width over its height, as it arrives. */
  readonly onAspect?: (ratio: number) => void;
}

/** Who this side shows yet, and how a tile says their sound and picture
 * arrived (QUEUE row 472): a person just met stays on "Connecting" until the
 * room says the other side has this side too, so both show at once. */
export const RevealContext = createContext<{ readonly revealed: ReadonlySet<string>; readonly ready: (id: string) => void } | null>(null);

/** How long a person's sound waits, silent, for their picture to show. */
const AUDIO_HOLD_MS = 8_000;

/** The video-link button over a phone call: a round glass glyph, matching
 * the rest of the call's icon-only controls (QUEUE row 120). */
const LINK_BUTTON_STYLE: CSSProperties = {
  display: "grid",
  placeItems: "center",
  width: 44,
  height: 44,
  borderRadius: "50%",
  border: "none",
  padding: 0,
  background: "rgba(58,58,60,0.62)",
  color: "#fff",
  backdropFilter: "blur(14px)",
  WebkitBackdropFilter: "blur(14px)",
  cursor: "pointer",
};

// Plays a remote participant's audio. An <audio> element takes no room in
// the tile's layout; a <video> here was a flex child that took a third of
// the tile's width away from the picture.
function AudioSink({ track, silent }: { readonly track: MediaStreamTrack; readonly silent?: boolean }): JSX.Element {
  const ref = useRef<HTMLAudioElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.srcObject = new MediaStream([track]);
    void el.play().catch(() => {});
    return keepPlaying(el);
  }, [track]);
  // Muted rather than stopped while it waits, so it is already playing the
  // moment the picture shows and the hearing check still sees it play.
  useEffect(() => {
    if (ref.current) ref.current.muted = !!silent;
  }, [silent]);
  return <audio ref={ref} autoPlay />;
}

/** True while a remote track has stopped bringing frames (the browser marks
 * it muted a moment after the packets stop: an app sent to the background,
 * a stalled connection). */
function useStalled(track: MediaStreamTrack | null): boolean {
  const [stalled, setStalled] = useState(() => !!track?.muted);
  const prevRef = useRef<MediaStreamTrack | null>(null);
  useEffect(() => {
    const swapped = !!prevRef.current && prevRef.current !== track;
    prevRef.current = track;
    if (!track) {
      setStalled(false);
      return;
    }
    // A track that replaces another (a re-pull of a frozen camera) starts
    // muted until its packets arrive. It gets a few seconds before it counts
    // as paused, so the tile keeps the last picture instead of dropping to
    // the initial and back (QUEUE row 452).
    let grace: ReturnType<typeof setTimeout> | undefined;
    const update = (): void => {
      if (grace) clearTimeout(grace);
      grace = undefined;
      setStalled(track.muted);
    };
    if (swapped && track.muted) grace = setTimeout(update, 4000);
    else update();
    track.addEventListener("mute", update);
    track.addEventListener("unmute", update);
    return () => {
      if (grace) clearTimeout(grace);
      track.removeEventListener("mute", update);
      track.removeEventListener("unmute", update);
    };
  }, [track]);
  return stalled;
}

/** True once a remote video track has brought a frame. A track just pulled
 * starts muted and unmutes on its first frame, so until then the tile is
 * still connecting rather than paused. */
function useHadFrame(track: MediaStreamTrack | null): boolean {
  const [had, setHad] = useState(() => !!track && !track.muted);
  useEffect(() => {
    if (!track) {
      setHad(false);
      return;
    }
    if (!track.muted) {
      setHad(true);
      return;
    }
    setHad(false);
    const on = (): void => setHad(true);
    track.addEventListener("unmute", on, { once: true });
    return () => track.removeEventListener("unmute", on);
  }, [track]);
  return had;
}

/** How long after a tile appears it may still say it is connecting. Past
 * this a guest who sends nothing (no camera, no microphone) shows the plain
 * tile rather than a ring that never stops. */
const CONNECTING_WINDOW_MS = 25_000;

/** Where a person just let in stands, from the room's word that they joined
 * to their first sound and picture (QUEUE row 451), or null once they are
 * through. Only for the first moments of the tile, so a re-pull mid-call
 * never brings it back. */
export type ConnectingStage = "joining" | "media" | "video" | "audio";

export function connectingStage(
  participant: RemoteTileData["participant"],
  audio: MediaStreamTrack | null,
  videoShowing: boolean,
): ConnectingStage | null {
  if (participant.phoneCall || participant.connectionState === "reconnecting") return null;
  const { tracks } = participant;
  if (!tracks.audio && !tracks.video) return "joining";
  const waitAudio = !!tracks.audio && !audio;
  const waitVideo = !!tracks.video && !participant.cameraOff && !videoShowing;
  if (waitAudio && waitVideo) return "media";
  if (waitVideo) return "video";
  if (waitAudio) return "audio";
  return null;
}

/** "ready": this side has them, and waits for the room to say they have
 * this side too. */
type ShownStage = ConnectingStage | "ready";

const STAGE_TEXT: Record<ShownStage, string> = {
  ready: "Connecting",
  joining: "Joining",
  media: "Connecting audio and video",
  video: "Connecting video",
  audio: "Connecting audio",
};

export function RemoteTile({ tile, compact, onHangUpPhone, videoLink, onLowerHand, onAspect }: RemoteTileProps): JSX.Element {
  const { participant } = tile;
  const phoneCall = participant.phoneCall;
  // The phone's audio arrives once they pick up; until then it is ringing.
  const phoneWaiting = !!phoneCall && !participant.tracks.audio;
  const reconnecting = participant.connectionState === "reconnecting";
  const away = !!participant.away && !reconnecting;
  const stalled = useStalled(tile.videoTrack);
  const hadFrame = useHadFrame(tile.videoTrack);
  // A frozen or black picture says nothing about whether they are there, so
  // a stalled camera shows their initial and a line saying where they are.
  // A person who has gone to another screen keeps their last picture up for as
  // long as their camera is on (Adam, 2026-11-02: "continue to show video while
  // the person is on another screen, as long as their camera is on"); the Away
  // line says where they are.
  const showVideo = !!tile.videoTrack && !participant.cameraOff && (!stalled || (away && hadFrame));
  // The first moments after someone is let in: a ring and the step they are
  // on, until their sound and picture arrive.
  const [fresh, setFresh] = useState(true);
  useEffect(() => {
    const t = setTimeout(() => setFresh(false), CONNECTING_WINDOW_MS);
    return () => clearTimeout(t);
  }, []);
  const mediaStage = connectingStage(participant, tile.audioTrack, showVideo && hadFrame);
  const reveal = useContext(RevealContext);
  const held = !!reveal && !phoneCall && !reveal.revealed.has(participant.id);
  useEffect(() => {
    if (!mediaStage && reveal && !phoneCall) reveal.ready(participant.id);
  }, [mediaStage, reveal, phoneCall, participant.id]);
  const rawStage: ShownStage | null = mediaStage ?? (held ? "ready" : null);
  const [through, setThrough] = useState(false);
  useEffect(() => {
    if (!rawStage) setThrough(true);
  }, [rawStage]);
  const stage = fresh && !through ? rawStage : null;
  // Their sound waits with their picture, for a few seconds at most.
  const [audioFree, setAudioFree] = useState(false);
  useEffect(() => {
    if (!tile.audioTrack) return;
    const t = setTimeout(() => setAudioFree(true), AUDIO_HOLD_MS);
    return () => clearTimeout(t);
  }, [tile.audioTrack]);
  const status = reconnecting || stage
    ? null
    : away && !showVideo
      ? "Still on the call, Breeze is in the background"
      : !showVideo && stalled && !participant.cameraOff
        ? "Video paused"
        : null;

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        background: "#12161b",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      {tile.audioTrack ? <AudioSink track={tile.audioTrack} silent={!!stage && !audioFree} /> : null}
      {showVideo && !stage ? (
        <VideoView track={tile.videoTrack} fit="cover" muted onAspect={onAspect} />
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "var(--space-3)",
            padding: "0 var(--space-4)",
            textAlign: "center",
          }}
        >
          <div
            data-connecting={stage ?? undefined}
            style={{
              position: "relative",
              width: 88,
              height: 88,
              borderRadius: "50%",
              background: stage ? "#1d232a" : "#2a323b",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 32,
              fontWeight: 600,
              color: "#e7eaee",
            }}
          >
            {stage ? <span className="breeze-connecting-ring" aria-hidden="true" /> : null}
            <span style={{ opacity: stage ? 0.55 : 1 }}>
              {phoneCall ? <PhoneIcon size={36} /> : (participant.name.trim()[0] ?? "?").toUpperCase()}
            </span>
          </div>
          {stage && !compact ? (
            <div role="status" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 10, marginTop: 6 }}>
              <div style={{ color: "#e7eaee", fontSize: 15 }}>{STAGE_TEXT[stage]}</div>
              <div style={{ display: "flex", gap: 6 }} aria-hidden="true">
                <span className="breeze-connecting-step" data-on="1" />
                <span className="breeze-connecting-step" data-on={stage === "joining" ? "0" : "1"} />
                <span className="breeze-connecting-step" data-on={stage === "video" || stage === "audio" || stage === "ready" ? "1" : "0"} />
              </div>
            </div>
          ) : null}
          {status && !compact ? <div style={{ color: "#c7ccd2", fontSize: 15 }}>{status}</div> : null}
        </div>
      )}

      <div
        data-corner-avoid={compact ? undefined : "1"}
        style={{
          position: "absolute",
          left: compact ? "var(--space-1)" : "var(--space-3)",
          ...(compact
            ? { bottom: "var(--space-1)" }
            : { top: "calc(env(safe-area-inset-top, 0px) + var(--space-3))" }),
          display: "flex",
          alignItems: "center",
          gap: "var(--space-2)",
          padding: "6px 12px",
          borderRadius: "var(--radius-sm)",
          background: "rgba(10,13,17,0.55)",
          color: "#fff",
          fontSize: 14,
        }}
      >
        <span>{participant.name}</span>
        {phoneWaiting ? <span style={{ color: "#c7ccd2" }}>Calling</span> : null}
        {stage && compact ? <span style={{ color: "#c7ccd2" }}>Connecting</span> : null}
        {away && (showVideo || compact) ? <span style={{ color: "#c7ccd2" }}>Away</span> : null}
        {participant.muted ? <MicOffIcon size={16} /> : null}
        {participant.handRaisedAt ? (
          <HandChip name={participant.name} onLower={onLowerHand ? () => onLowerHand(participant.id) : undefined} />
        ) : null}
      </div>

      {phoneCall && onHangUpPhone && videoLink && !compact ? (
        <div style={{ position: "absolute", bottom: "calc(env(safe-area-inset-bottom, 0px) + 152px)", left: "50%", transform: "translateX(-50%)" }}>
          {canShare() ? (
            <button
              type="button"
              aria-label="Send video link"
              title="Send video link"
              onClick={() => shareLink(videoLink, "Join my call with video")}
              style={LINK_BUTTON_STYLE}
            >
              <LinkIcon size={20} />
            </button>
          ) : (
            <CopyButton text={videoLink} label="Copy video link" icon={<LinkIcon size={20} />} style={LINK_BUTTON_STYLE} />
          )}
        </div>
      ) : null}

      {phoneCall && onHangUpPhone ? (
        <button
          type="button"
          aria-label={`Hang up on ${participant.name}`}
          title={`Hang up on ${participant.name}`}
          onClick={() => onHangUpPhone(phoneCall)}
          style={{
            position: "absolute",
            ...(compact
              ? { top: "var(--space-1)", right: "var(--space-1)" }
              : { bottom: "calc(env(safe-area-inset-bottom, 0px) + 96px)", left: "50%", transform: "translateX(-50%)" }),
            display: "flex",
            alignItems: "center",
            gap: 8,
            height: compact ? 32 : 44,
            padding: compact ? "0 8px" : "0 16px",
            borderRadius: compact ? 8 : 12,
            border: "none",
            background: "var(--danger)",
            color: "#fff",
            font: "inherit",
            fontSize: 15,
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          <HangUpIcon size={compact ? 18 : 22} />
          {compact ? null : <span>Hang up</span>}
        </button>
      ) : null}

      {reconnecting ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(10,13,17,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#fff",
            fontSize: 14,
            fontWeight: 500,
          }}
        >
          Reconnecting
        </div>
      ) : null}

      {participant.cameraOff && !phoneCall && !stage && showVideo === false && tile.videoTrack === null ? (
        <div style={{ position: "absolute", top: "var(--space-3)", right: "var(--space-3)", color: "#c7ccd2" }}>
          <CameraOffIcon size={16} />
        </div>
      ) : null}
    </div>
  );
}
